import { useEffect, useState } from 'react'
import { Handle, Position, type NodeProps } from 'reactflow'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { GitBranch } from 'lucide-react'
import type { CondicionData } from '../../types'
import { NodeDeleteButton } from './NodeDeleteButton'

interface CondicionNodeProps extends NodeProps {
  data: CondicionData & {
    onChange?: (data: Partial<CondicionData>) => void
    onDelete?: () => void
  }
}

export function CondicionNode({ data }: CondicionNodeProps) {
  // Estado local del campo de días como string, para permitir borrarlo/dejarlo
  // vacío mientras se escribe (un input numérico controlado forzaría 0 y no
  // dejaría borrar).
  const [diasStr, setDiasStr] = useState(String(data.esperar_dias ?? 1))
  useEffect(() => {
    setDiasStr(String(data.esperar_dias ?? 1))
  }, [data.esperar_dias])

  return (
    <div className="w-[280px] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-background shadow-sm overflow-hidden">
      <Handle type="target" position={Position.Top} className="!h-2.5 !w-2.5 !bg-purple-500 !border-2 !border-white dark:!border-background" />

      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-purple-500/10 border-b border-purple-500/20">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-purple-500/15 text-purple-600 dark:text-purple-400">
          <GitBranch size={13} />
        </span>
        <span className="flex-1 truncate text-xs font-semibold text-purple-700 dark:text-purple-300">
          Condición
        </span>
        <NodeDeleteButton onDelete={data.onDelete} nombre="Condición" />
      </div>

      {/* Body */}
      <div className="space-y-2 p-3">
        <div>
          <Label className="text-[11px] text-gray-500">Si el contacto…</Label>
          <Select
            value={data.condicion || 'abrió'}
            onValueChange={(v) => data.onChange?.({ condicion: v as CondicionData['condicion'] })}
          >
            <SelectTrigger className="nodrag h-7 text-xs mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="abrió">Abrió el correo</SelectItem>
              <SelectItem value="no_abrió">No abrió el correo</SelectItem>
              <SelectItem value="click_cualquiera">Hizo click en cualquier link</SelectItem>
              <SelectItem value="click_link">Hizo click en un link específico</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {data.condicion === 'click_link' && (
          <div>
            <Label className="text-[11px] text-gray-500">Link</Label>
            {data.links_disponibles && data.links_disponibles.length > 0 ? (
              <Select
                value={data.link_url || ''}
                onValueChange={(v) => data.onChange?.({ link_url: v })}
              >
                <SelectTrigger className="nodrag h-7 text-xs mt-1">
                  <SelectValue placeholder="Seleccionar link" />
                </SelectTrigger>
                <SelectContent>
                  {data.links_disponibles.map((url) => (
                    <SelectItem key={url} value={url} className="text-xs max-w-[240px] truncate">
                      {url}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                className="nodrag h-7 text-xs mt-1"
                value={data.link_url || ''}
                onChange={(e) => data.onChange?.({ link_url: e.target.value })}
                placeholder="https://… (o escribe el HTML con links)"
              />
            )}
          </div>
        )}

        <div>
          <Label className="text-[11px] text-gray-500">Días de espera antes de evaluar</Label>
          <Input
            type="number"
            min={0}
            className="nodrag h-7 text-xs mt-1"
            value={diasStr}
            onChange={(e) => {
              const v = e.target.value
              setDiasStr(v)
              if (v === '') return // permitir vacío mientras escribe
              const n = parseInt(v, 10)
              if (!isNaN(n) && n >= 0) data.onChange?.({ esperar_dias: n })
            }}
            onBlur={() => {
              const n = parseInt(diasStr, 10)
              if (diasStr === '' || isNaN(n) || n < 0) {
                setDiasStr('0')
                data.onChange?.({ esperar_dias: 0 })
              }
            }}
          />
          <p className="mt-0.5 text-[10px] text-gray-400">
            {(data.esperar_dias ?? 1) === 0
              ? 'Revisa en el próximo ciclo, sin dar tiempo a que abran'
              : `Espera ${data.esperar_dias} día${data.esperar_dias === 1 ? '' : 's'} desde el envío antes de revisar`}
          </p>
        </div>

        {/* Leyenda de ramas */}
        <div className="flex justify-between px-1 pt-1 text-[10px] font-medium">
          <span className="text-green-600 dark:text-green-400">● SÍ cumple</span>
          <span className="text-gray-400">NO cumple ●</span>
        </div>
      </div>

      {/* Handles de salida: SÍ (izquierda) y NO (derecha) */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="si"
        style={{ left: '25%' }}
        className="!h-2.5 !w-2.5 !bg-green-500 !border-2 !border-white dark:!border-background"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="no"
        style={{ left: '75%' }}
        className="!h-2.5 !w-2.5 !bg-gray-400 !border-2 !border-white dark:!border-background"
      />
    </div>
  )
}
